<script setup>
// Agent (chatbot) picker.
//
// A dropdown rather than a grid of cards: the agent is picked once and then confirmed,
// while the template grid below is what the operator actually browses. Cards for both
// pushed the templates under the fold.
//
// The selected agent still shows its avatar and number underneath, because that is how an
// operator confirms they are about to message on the right brand's behalf — the same face
// the contact sees on WhatsApp. Losing that to a text-only <select> is what the grid was
// protecting; the confirmation row keeps it without spending the screen.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import globalConstants from 'dashboard/constants/globals.js';

const props = defineProps({
  chatbots: { type: Array, default: () => [] },
  modelValue: { type: String, default: '' },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const options = computed(() =>
  props.chatbots.map(bot => ({
    value: bot.id,
    label: bot.chatbot_phone ? `${bot.name} · ${bot.chatbot_phone}` : bot.name,
  }))
);

const selected = computed(() =>
  props.chatbots.find(bot => bot.id === props.modelValue)
);

// Two initials, so an agent with no avatar still reads as a distinct face.
function initials(name) {
  return (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

// ChatData's dashboard keys on its own chatbot id, not the Firestore document id we use
// everywhere else. Agents filed without one simply get no link.
const chatdataUrl = computed(() =>
  selected.value?.chatdata_id
    ? `${globalConstants.CHATDATA_DASHBOARD_URL}/${selected.value.chatdata_id}`
    : ''
);
</script>

<template>
  <div>
    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.AGENT.LOADING') }}
    </p>

    <p v-else-if="!chatbots.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.AGENT.EMPTY') }}
    </p>

    <ComboBox
      v-else
      :model-value="modelValue"
      :options="options"
      :placeholder="t('OUTBOUNDS.AGENT.PLACEHOLDER')"
      :search-placeholder="t('OUTBOUNDS.AGENT.SEARCH')"
      :empty-state="t('OUTBOUNDS.AGENT.EMPTY')"
      class="w-full max-w-md"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <!-- The picked agent as a chat balloon, matching the preview further down the page:
         this is the face and the number the contact will see, so it is shown the way the
         contact will see it, not as a form field's echo. -->
    <div v-if="selected" class="relative mt-4 ml-2 max-w-md">
      <span
        class="absolute top-2 -left-1.5 size-3 bg-n-teal-3 [clip-path:polygon(100%_0,0_0,100%_100%)]"
      />
      <div
        class="relative flex items-center gap-3 px-3 py-2.5 border rounded-2xl rounded-tl-none border-n-teal-6 bg-n-teal-3"
      >
        <img
          v-if="selected.url_image"
          :src="selected.url_image"
          :alt="selected.name"
          class="object-cover rounded-full size-11 shrink-0 bg-n-alpha-2"
        />
        <span
          v-else
          class="flex items-center justify-center text-sm font-semibold rounded-full size-11 shrink-0 bg-n-teal-5 text-n-teal-11"
        >
          {{ initials(selected.name) }}
        </span>

        <span class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ selected.name }}
          </span>
          <span class="text-xs truncate tabular-nums text-n-slate-11">
            {{ selected.chatbot_phone || t('OUTBOUNDS.AGENT.NO_PHONE') }}
          </span>
        </span>

        <a
          v-if="chatdataUrl"
          :href="chatdataUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 ml-auto text-xs shrink-0 text-n-slate-11 hover:text-n-slate-12"
        >
          <i class="i-lucide-external-link size-3.5" />
          {{ t('OUTBOUNDS.AGENT.OPEN_IN_CHATDATA') }}
        </a>
      </div>
    </div>
  </div>
</template>
